import { WindowManager } from './WindowManager';
import { EventListener } from 'src/models/eventManager/EventListener';
import { EventSource } from 'src/models/eventManager/EventSource';
import { EventSubscription } from 'src/models/eventManager/EventSubscription';
import { EventTypeDetail } from 'src/models/eventManager/EventTypeDetail';
import { EventTypes } from 'src/models/eventManager/EventTypes';

export class SourceEventManager {

    static generateKey(eventSource: EventSource, disableAppId = false) {
		const sourceKeyOrder: Array<(keyof EventSource)> = [
			'templateId',
			'applicationCode',
			'configurationKey',
			'styleKey',
			'applicationId',
		];
		return sourceKeyOrder.reduce((finalKey: string, sourceKey: string) => {
			if (sourceKey === 'applicationId' && disableAppId) {
				return finalKey;
			}
			const defaultValue = sourceKey === 'configurationKey' || sourceKey === 'styleKey' ? 'default' : undefined;
			return SourceEventManager.generateKeyPart(finalKey, eventSource[sourceKey], defaultValue);
		}, '');
	}

	static generateKeyPart(currentString: string, value?: string, defaultValue?: string) {
		const workingValue = value || defaultValue;
		if (!workingValue) {
			return currentString;
		}
		if (currentString) {
			return `${currentString}-${workingValue}`;
		}
		return workingValue;
	}

	/**
	 * Used to compare the event source with the template configuration provided in the App Manager's `appsRenderOptons.groups.apps`
	 *
	 * @param listener
	 * @param publisher
	 * @returns
	 */
	static compareSources(listener: EventSource, publisher: EventSource) {
		if (!listener || !publisher) {
			return false;
		}
		if (publisher.templateId !== listener.templateId) {
			return false;
		}
		if (publisher.configurationKey && publisher.configurationKey !== listener.configurationKey) {
			return false;
		}
		if (publisher.styleKey && publisher.styleKey !== listener.styleKey) {
			return false;
		}
		if (publisher.applicationCode && publisher.applicationCode !== listener.applicationCode) {
			return false;
		}
		if (publisher.version && publisher.version !== listener.version) {
			return false;
		}
		if (publisher.applicationId && listener.applicationId && publisher.applicationId !== listener.applicationId) {
			return false;
		}
		return true;
	}

    public key: string;
    private subs: Map<EventTypes, Array<EventSubscription<any, any>>> = new Map();

    constructor(sourceKey: string) {
		this.key = sourceKey;
	}

	public getSubscribers<TType extends EventTypes, TDetail extends EventTypeDetail>(
		type: TType,
	): Array<EventSubscription<TType, TDetail>> {
		if (!this.subs || !this.isValidType(type)) {
			return [];
		}
		return (this.subs.get(type) as Array<EventSubscription<TType, TDetail>>) || [];
	}

	public subscribe<TType extends EventTypes, TDetail extends EventTypeDetail>(
		subscription: EventSubscription<TType, TDetail>,
	): void {
		const { type, listener } = subscription;
		if (!type || !this.isValidType(type) || !listener || !this.getListenerKey(listener)) {
			return;
		}
		const existingSubscribers = this.getSubscribers(type);
		const existingSubscription = existingSubscribers.find(
			(exSubscription) =>
				this.getListenerKey(exSubscription.listener) === this.getListenerKey(listener),
		);
		if (!existingSubscription) {
			this.pushSubscribers(subscription);
		}
	}

	public unsubscribe<TType extends EventTypes, TDetail extends EventTypeDetail>(
		type: TType,
		listener: EventListener,
	): void {
		if (!this.isValidType(type)) {
			return;
		}
		const existingSubscribers = this.getSubscribers<TType, TDetail>(type);
		if (existingSubscribers.length > 0) {
			const updatedSubscribers = existingSubscribers.reduce((list: Array<EventSubscription<TType, TDetail>>, item) => {
				if (this.getListenerKey(item.listener) !== this.getListenerKey(listener)) {
					list.push(item);
				}
				return list;
			}, []);
			this.setSubscribers(type, updatedSubscribers);
		}
	}

	public publish<TDetail extends EventTypeDetail, TEventSource extends EventSource>(
		source: TEventSource,
		detail: TDetail,
	): void {
		const { type } = detail;
		if (!this.isValidType(type)) {
			console.error('SourceEventManager: Invalid Event Type', type);
			return;
		}
		const subscribers = this.getSubscribers(type);
		if (subscribers) {
			subscribers.forEach((subscriber) => {
				if (!SourceEventManager.compareSources(subscriber.listener, source)) {
					subscriber.onPublish({
						detail,
						source,
					});
				}
			});
		}
		// Add event details to window storage
		const eventDetails = WindowManager.has('eventDetails') ? WindowManager.get('eventDetails') : {};
		eventDetails[this.key] = {
			...eventDetails[this.key],
			[type]: detail,
		};
		WindowManager.set('eventDetails', eventDetails);
	}

	private pushSubscribers<TType extends EventTypes, TDetail extends EventTypeDetail>(
		sub: EventSubscription<TType, TDetail>,
	): void {
		const existing = this.getSubscribers(sub.type);
		this.subs.set(sub.type, [...existing, sub]);
	}

	private setSubscribers<TType extends EventTypes, TDetail extends EventTypeDetail>(
		type: TType,
		subs: Array<EventSubscription<TType, TDetail>>,
	): void {
		const workingSubs = subs.filter((sub) => sub.type === type);
		this.subs.set(type, workingSubs);
	}

	private isValidType(type: unknown): type is EventTypes {
		return Object.values(EventTypes).includes(type as EventTypes);
	}

	private getListenerKey(listener: EventListener) {
		return SourceEventManager.generateKey(listener);
	}
}



// WEBPACK FOOTER //
// ./src/utils/SourceEventManager.ts